import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  GaugeIcon,
  UsersThreeIcon,
  BellRingingIcon,
  ShieldCheckIcon,
  ShieldSlashIcon,
} from "@phosphor-icons/react";
import { fetchSettings, updateSettings } from "../lib/api";
import { toast } from "sonner";

/**
 * Fixed bottom nav bar visible only on mobile (< lg).
 * Gives instant access to the most-used pages and a one-tap
 * AI kill switch — exactly what you need when a token-burn pushover
 * lands on your phone and you want to react in 2 seconds.
 */
export default function MobileBottomNav() {
  const [aiEnabled, setAiEnabled] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    fetchSettings()
      .then((s) => setAiEnabled(!!s.ai_enabled))
      .catch(() => {});

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  const toggleAI = async (e) => {
    e.preventDefault();
    if (aiEnabled === null) return;
    if (aiEnabled && !window.confirm("Disable AI fallback (hard kill)?")) return;
    setBusy(true);
    try {
      const s = await fetchSettings();
      const updated = await updateSettings({ ...s, ai_enabled: !s.ai_enabled });
      setAiEnabled(!!updated.ai_enabled);
      toast.success(updated.ai_enabled ? "AI enabled" : "AI killed");
    } catch {
      toast.error("Could not toggle");
    } finally {
      setBusy(false);
    }
  };

  const items = [
    { to: "/", icon: GaugeIcon, label: "Dash", testId: "mnav-dashboard", end: true },
    { to: "/groups", icon: UsersThreeIcon, label: "Groups", testId: "mnav-groups" },
    { to: "/notifications", icon: BellRingingIcon, label: "Alerts", testId: "mnav-alerts" },
  ];

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-zinc-200 bg-white pb-[env(safe-area-inset-bottom)]"
      style={{ boxShadow: "0 -4px 12px rgba(0,0,0,0.04)" }}
    >
      <div className="grid grid-cols-4">
        {items.map(({ to, icon: Icon, label, testId, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${
                isActive ? "text-zinc-950" : "text-zinc-400 hover:text-zinc-700"
              }`
            }
          >
            <Icon size={20} weight="bold" />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          data-testid="mnav-killswitch"
          onClick={toggleAI}
          disabled={busy || aiEnabled === null}
          className={`flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${
            aiEnabled === null
              ? "text-zinc-300"
              : aiEnabled
              ? "text-zinc-950"
              : "text-[#FF3B30]"
          }`}
          aria-label="Toggle AI kill switch"
        >
          {aiEnabled ? (
            <ShieldCheckIcon size={20} weight="fill" />
          ) : (
            <ShieldSlashIcon size={20} weight="fill" />
          )}
          <span>{aiEnabled === null ? "AI…" : aiEnabled ? "AI ON" : "Killed"}</span>
        </button>
      </div>
    </nav>
  );
}
